// Admin auth helpers — httpOnly cookie-based (JWT lives in server-set cookie).
// Frontend only tracks a non-sensitive "authenticated" flag in sessionStorage
// so the UI can synchronously decide whether to show admin routes.
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;
const FLAG_KEY = "sws_admin_authed";
const IS_DEV = process.env.NODE_ENV !== "production";

// All admin requests must include the httpOnly cookie.
const client = axios.create({
  baseURL: API,
  withCredentials: true,
  validateStatus: () => true,
});

function logDev(context, error) {
  if (IS_DEV) {
    console.warn(`[adminAuth] ${context}`, error);
  }
}

function setFlag(v) {
  try {
    if (v) sessionStorage.setItem(FLAG_KEY, "1");
    else sessionStorage.removeItem(FLAG_KEY);
  } catch (error) {
    logDev("flag write failed", error);
  }
}

export function isLoggedIn() {
  try {
    return sessionStorage.getItem(FLAG_KEY) === "1";
  } catch (error) {
    logDev("flag read failed", error);
    return false;
  }
}

export async function login(username, password) {
  const res = await client.post("/admin/login", { username, password });
  if (res.status >= 400) {
    const detail = res.data?.detail || "Login failed";
    const err = new Error(detail);
    err.response = res;
    throw err;
  }
  setFlag(true);
  return true;
}

export async function logout() {
  setFlag(false);
  try {
    await client.post("/admin/logout");
  } catch (error) {
    logDev("logout request failed", error);
  }
}

async function adminRequest(method, path, body) {
  const res = await client.request({ method, url: path, data: body });
  if (res.status === 401) {
    setFlag(false);
    throw new Error("unauthorized");
  }
  if (res.status >= 400) {
    throw new Error(res.data?.detail || "Request failed");
  }
  return res.data;
}

export function adminGet(path) {
  return adminRequest("get", path);
}

export function adminPost(path, body) {
  return adminRequest("post", path, body);
}

export function adminPut(path, body) {
  return adminRequest("put", path, body);
}

// Verify existing cookie session on app load (used by route guards).
export async function verifySession() {
  try {
    const res = await client.get("/admin/me");
    if (res.status === 200) {
      setFlag(true);
      return true;
    }
  } catch (error) {
    logDev("verifySession failed", error);
  }
  setFlag(false);
  return false;
}
